<template>
  <div
    ref="voiceDetectionIndicator"
    class="s-voiceDetectionIndicator">
    <canvas
      ref="micIndicatorCanvas"
      width="44"
      height="44" />
  </div>
</template>

<script setup>
import {
  ref, onMounted, onUnmounted, watch,
} from 'vue'

import store from '@/store'

const props = defineProps({
  stream: {
    default: null,
  },
  currentState: {
    type: String,
    default: null,
  },
  trackAudioLevel: {
    type: Boolean,
    default: false,
  },
})

const micIndicatorCanvas = ref(null)
const voiceDetectionIndicator = ref(null)
const audioContext = ref(null)
const analyzer = ref(null)
const frequencyData = ref(new Uint8Array(32))

function initAudioContext() {
  if (audioContext.value) {
    return
  }
  audioContext.value = new window.AudioContext()
  const source = audioContext.value.createMediaStreamSource(props.stream.clone())
  analyzer.value = audioContext.value.createAnalyser()

  source.connect(analyzer.value)

  analyzer.value.fftSize = 32
  analyzer.value.smoothingTimeConstant = 0.7
}

let ctx = null
const width = 44
const height = 44
const centerX = width / 2
const centerY = height / 2

let animationFrameId
let time = 0

// --- Helper function to draw a wavy circle ---
// Removed dynamicFrequencyFactor. Added subtle amplitude modulation for non-symmetry.
function drawWavyCircle(baseRadius, waveAmplitude, waveFrequency, phase, color1, color2, numPoints = 50) { // Reduced numPoints slightly for smaller canvas
  ctx.beginPath()
  const angleStep = (Math.PI * 2) / numPoints

  for (let i = 0; i <= numPoints; i++) {
    const angle = i * angleStep
    // Subtle modulation of amplitude for each point to break perfect symmetry
    // This makes the wave shape slightly irregular and change over time without altering wave count.
    const amplitudeModulation = (waveAmplitude * 0.2) * Math.sin((time * 0.025) + (angle * 2.5)) // Modulates up to 20% of waveAmplitude
    const currentAmplitude = waveAmplitude + amplitudeModulation

    const r = baseRadius + (currentAmplitude * Math.sin((angle * waveFrequency) + phase))
    const x = centerX + (r * Math.cos(angle))
    const y = centerY + (r * Math.sin(angle))
    if (i === 0) {
      ctx.moveTo(x, y)
    } else {
      ctx.lineTo(x, y)
    }
  }
  ctx.closePath()

  const gradient = ctx.createRadialGradient(centerX, centerY, Math.max(1, baseRadius * 0.1), centerX, centerY, baseRadius + waveAmplitude)
  gradient.addColorStop(0, color1)
  gradient.addColorStop(1, color2)

  ctx.fillStyle = gradient
  ctx.fill()
}

function drawInitializing() {
  const pulseSpeed = 0.002
  const pulseFactor = (Math.sin(time * pulseSpeed)) / 2

  const minRadius = 4 // Scaled down
  const maxRadius = 40 // Scaled down
  const currentRadius = minRadius + ((maxRadius - minRadius) * pulseFactor)

  const waveAmplitude = 1 // Scaled down
  const waveFrequency = 1 // Number of waves
  const wavePhase = time * 0.005 // Rotation speed

  drawWavyCircle(currentRadius, waveAmplitude, waveFrequency, wavePhase, 'rgba(200, 200, 200, 0.7)', 'rgba(190, 190, 190, 0.3)')
}

function drawListening() {
  if (!analyzer.value) {
    return
  }
  analyzer.value.getByteFrequencyData(frequencyData.value)
  const average = frequencyData.value.reduce((sum, val) => sum + val, 0) / frequencyData.value.length
  const loudnessPercentage = Math.min(Math.max(average / 100, 0), 1)
  const minPercentage = 0.3
  const maxPercentage = 0.8
  const finalScalePercentage = minPercentage + ((maxPercentage - minPercentage) * loudnessPercentage)

  const numCircles = 3
  const baseRadii = [60, 44, 35]
  const waveAmplitudes = [1, 1.2, 1.5]
  const waveFrequencies = [6, 5, 7] // Number of waves for each circle
  const phaseSpeeds = [0.05, 0.06, 0.07] // Rotation speeds
  const primaryColor = store.getters['auth/config'].colors.primary
  const primaryColorString = `${primaryColor.r}, ${primaryColor.g}, ${primaryColor.b}`
  if (props.trackAudioLevel) {
    const shadowLevel = Math.min(Math.max(average / 45, 0), 1)
    const shadowBlur = 20 + (20 * shadowLevel)
    const shadowSpread = 13 * shadowLevel
    voiceDetectionIndicator.value.style.boxShadow = `0 3px 3px 0 rgba(8, 8, 8, 0.12), -6px 6px 4.5px 0 rgba(8, 8, 8, 0.04), 0 0 ${shadowBlur}px ${shadowSpread}px rgba(${primaryColorString}, 0.8)`
  }
  const colors = [
    [`rgba(${primaryColorString}, 0.7)`, `rgba(${primaryColorString}, 0.2)`],
    [`rgba(${primaryColorString}, 0.6)`, `rgba(${primaryColorString}, 0.3)`],
    [`rgba(${primaryColorString}, 0.5)`, `rgba(${primaryColorString}, 0.4)`],
  ]

  for (let i = 0; i < numCircles; i++) {
    const radius = baseRadii[i] * finalScalePercentage
    const wavePhase = time * phaseSpeeds[i]
    drawWavyCircle(
      Math.max(3, radius), // Ensure radius doesn't get too small
      waveAmplitudes[i] * finalScalePercentage,
      waveFrequencies[i],
      wavePhase,
      colors[i][0],
      colors[i][1],
    )
  }
}

function drawProcessing() {
  const pulseSpeed = 0.003
  const pulseFactor = (Math.sin(time * pulseSpeed) + 1) / 2

  const minRadius = 8 // Scaled down
  const maxRadius = 16 // Scaled down
  const currentRadius = minRadius + ((maxRadius - minRadius) * pulseFactor)

  const waveAmplitude = 0.5 // Scaled down
  const waveFrequency = 7 // Number of waves
  const wavePhase = time * -0.1 // Rotation speed

  drawWavyCircle(currentRadius, waveAmplitude, waveFrequency, wavePhase, 'rgba(200, 200, 200, 0.7)', 'rgba(190, 190, 190, 0.3)')
}

// --- Main animation loop ---
function animate() {
  time++
  ctx.clearRect(0, 0, width, height)

  switch (props.currentState) {
    case 'initializing':
      drawInitializing()
      break
    case 'listening':
      drawListening()
      break
    case 'processing':
      drawProcessing()
      break
    default:
      break
  }

  animationFrameId = requestAnimationFrame(animate)
}

watch(() => props.currentState, (state) => {
  if (state === 'listening') {
    initAudioContext()
  }
}, { immediate: true })

onMounted(() => {
  ctx = micIndicatorCanvas.value.getContext('2d')
  animate()
})

onUnmounted(() => {
  cancelAnimationFrame(animationFrameId)
  if (audioContext.value) {
    audioContext.value.close()
  }
})
</script>

<style lang="scss" scoped>
@import '@/assets/scss/config';

.s-voiceDetectionIndicator {
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  overflow: hidden;
}
</style>
